import Image from '@commons/image/Image'
import Price from '@commons/product/price/Price'
import { CampaignProps, CopywritingShopTheLooks } from '@commons/types'
import { usePbProps } from '@content/helpers/src/context'
import { useProducts } from '@content/helpers/src/contextProducts'
import { stripTags } from '@content/helpers/src/utils'
import { NapiProduct, viewItemList } from '@nespresso/catalog-sdk'
import AddToCartButton from '@commons/add-to-cart/AddToCartButton'
import styles from './ShopTheLook.module.scss'
import { LayoutProps } from '@commons/drupal.types'

export default function ShopTheLook({ disableTracking = false }) {
    const { products } = useProducts()
    const {
        copywriting,
        campaign,
        layout
    }: {
        copywriting: CopywritingShopTheLooks
        campaign: CampaignProps
        layout: LayoutProps
    } = usePbProps()

    if (products.length <= 0) {
        return null
    }

    const SKUs = products.map((product: NapiProduct) => product.internationalId)

    if (!disableTracking) {
        viewItemList(SKUs, {
            listId: campaign.position,
            listName: `${campaign.position} - ${copywriting?.title}`,
            eventRaisedBy: campaign.position
        })
    }

    const renderProductComponents = product => {
        const { name, images, price, headline } = product
        const renderImage = () => {
            if (!name || !images) {
                return null
            }

            return (
                <div className={styles['shop-the-look__placeholder']}>
                    <Image src={images.icon} alt={name} />
                </div>
            )
        }

        const renderProductName = () => {
            if (!name) {
                return null
            }

            return (
                <a
                    href={product.pdpURLs.desktop}
                    className={
                        layout.contrast === 'light'
                            ? styles['shop-the-look__product-info-title-dark']
                            : styles['shop-the-look__product-info-title-light']
                    }>
                    {name}
                </a>
            )
        }

        const renderProductHeadline = () => {
            if (!headline) {
                return null
            }

            return (
                <p
                    className={`t-xs-500-sl ${layout.contrast === 'dark' && styles['shop-the-look__product-info-title-light']}`}>
                    {stripTags(headline)}
                </p>
            )
        }

        const renderProductPlaceholder = () => {
            return (
                <div className={styles['shop-the-look__product-info']}>
                    {renderProductName()}
                    {renderProductHeadline()}

                    <div className={styles['shop-the-look__price']}>
                        <Price
                            price={price}
                            priceDark={layout.contrast === 'light'}
                            priceLight={layout.contrast === 'dark'}
                        />
                    </div>
                </div>
            )
        }

        const renderProductATC = () => {
            return (
                <>
                    <div className={styles['shop-the-look__atc']}>
                        <AddToCartButton
                            size="small"
                            product={product}
                            contrast={
                                layout.contrast === 'light' ? 'dark' : 'light'
                            }
                        />
                    </div>
                </>
            )
        }

        return (
            <div className={styles['shop-the-look']}>
                {renderImage()}
                <div className={styles['shop-the-look__container']}>
                    {renderProductPlaceholder()}
                    {renderProductATC()}
                </div>
            </div>
        )
    }

    return (
        <div className={styles['shop-the-look__wrapper']}>
            {products.map((product, index) => (
                <div key={index}>{renderProductComponents(product)}</div>
            ))}
        </div>
    )
}
